import {
	BigNum,
	PERCENTAGE_PRECISION,
	PERCENTAGE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { PerformanceBreakdownStat } from '../PerformanceBreakdownStat';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { GraphType, Period } from '../VaultGraphs';
import { useState } from 'react';
import { VaultDepositorHistoryGraph } from './VaultDepositorHistoryGraph';
import { HUNDRED } from 'src/constants/math';
import { useVaultSnapshotHistory } from 'src/hooks/query/vaults/base/useVaultSnapshotHistory';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import { getMaxDailyDrawdownFromHistory } from 'src/utils/vault/math';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import { useVaultOnChainData } from 'src/hooks/query/vaults/base/useVaultOnChainData';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { UiVaults } from 'src/constants/vaults';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import Divider from 'src/components/primitives/Divider/Divider';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

const UserPerformanceBreakdownStats = (props: {
	depositAssetConfig: SpotMarketConfig;
	vaultPubkey: string;
}) => {
	const { data: vaultStats } = useVaultStats(props.vaultPubkey);
	const { data: vaultSnapshots, isLoading: isVaultSnapshotsLoading } =
		useVaultSnapshotHistory(props.vaultPubkey);
	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const { data: historiesLookup, isLoading: isVaultDepositorHistoryLoading } =
		useVaultDepositorHistories();
	const vaultDepositorHistory = historiesLookup?.[props.vaultPubkey] ?? [];
	const { data: onChainData } = useVaultOnChainData();
	const { data: depositorAccounts, isLoading: isDepositorLoading } =
		useVaultDepositorAccounts();
	const vaultAccountData = onChainData?.[props.vaultPubkey]?.vaultAccountData;
	const vaultDepositorAccountData =
		depositorAccounts?.[props.vaultPubkey]?.vaultDepositor;
	const isVaultDepositorLoaded = !isDepositorLoading;

	const isLoading =
		!vaultAccountData ||
		!isVaultDepositorLoaded ||
		!vaultStats?.isOnChainStatsLoaded;
	const isMaxDailyDrawdownLoading =
		isVaultSnapshotsLoading || isVaultDepositorHistoryLoading;

	const precisionExp = props.depositAssetConfig.precisionExp;
	const marketSymbol = props.depositAssetConfig.symbol;

	const cumulativeNetDeposits = BigNum.from(
		vaultDepositorAccountData?.netDeposits ?? 0,
		precisionExp
	);
	const userVaultSharesPct = getUserVaultSharesPct();
	const feesPaid = BigNum.from(
		vaultDepositorAccountData?.profitShareFeePaid ?? 0,
		precisionExp
	);
	const highWaterMark = BigNum.from(
		vaultDepositorAccountData?.cumulativeProfitShareAmount ?? 0,
		precisionExp
	);
	const highWaterMarkWithCurrentDeposit = highWaterMark
		.add(
			BigNum.from(vaultDepositorAccountData?.totalDeposits ?? 0, precisionExp)
		)
		.sub(
			BigNum.from(vaultDepositorAccountData?.totalWithdraws ?? 0, precisionExp)
		);
	const maxDailyDrawdownPct = getMaxDailyDrawdownPct();

	function getUserVaultSharesPct() {
		if (!vaultDepositorAccountData || !vaultAccountData)
			return BigNum.from(0, PERCENTAGE_PRECISION_EXP);

		const userShares = vaultDepositorAccountData.vaultShares;
		const vaultShares = vaultAccountData.totalShares;

		if (vaultShares.eqn(0)) {
			return BigNum.from(0, PERCENTAGE_PRECISION_EXP);
		}

		const userVaultSharesPct = userShares
			.mul(PERCENTAGE_PRECISION)
			.div(vaultShares);

		return BigNum.from(userVaultSharesPct, PERCENTAGE_PRECISION_EXP).mul(
			HUNDRED
		);
	}

	function getMaxDailyDrawdownPct() {
		if (
			!vaultDepositorAccountData ||
			!vaultAccountData ||
			!vaultSnapshots?.length ||
			!vaultDepositorHistory?.length
		)
			return 0;

		const firstDepositorTxn = vaultDepositorHistory?.[0];
		const relevantVaultSnapshots = vaultSnapshots
			?.filter((snapshot) => +snapshot.ts >= +firstDepositorTxn?.ts)
			.sort((a, b) => +a.ts - +b.ts);

		const maxDailyDrawdown = getMaxDailyDrawdownFromHistory(
			relevantVaultSnapshots,
			(uiVaultConfig?.isNotionalGrowthStrategy ?? false)
				? 'totalAccountQuoteValue'
				: 'totalAccountBaseValue'
		);

		return maxDailyDrawdown * 100;
	}

	return (
		<div className="flex w-full flex-col gap-y-md">
			<div className="flex flex-1 flex-col justify-start gap-y-md sm:flex-row">
				<PerformanceBreakdownStat
					label="Your Cumulative Net Deposits"
					value={`${cumulativeNetDeposits.prettyPrint()}`}
					marketSymbol={marketSymbol}
					loading={isLoading}
					className="sm:w-[33%]"
				/>
				<PerformanceBreakdownStat
					label="Vault Shares"
					value={`${userVaultSharesPct.prettyPrint()}%`}
					loading={isLoading}
					tooltip={'Your proportion of ownership in the vault.'}
					className="sm:w-[33%]"
				/>
				<PerformanceBreakdownStat
					label="Max Daily Drawdown"
					value={`${maxDailyDrawdownPct.toFixed(2)}%`}
					loading={isMaxDailyDrawdownLoading}
					tooltip={
						"The maximum day-to-day loss in the user's history in the vault."
					}
					className="sm:w-[33%]"
				/>
			</div>
			<div className="flex flex-1 flex-col justify-start gap-y-md sm:flex-row">
				<PerformanceBreakdownStat
					label="Fees Paid"
					value={`${feesPaid.prettyPrint()}`}
					marketSymbol={marketSymbol}
					loading={isLoading}
					tooltip="These are the total fees you have paid to the vault. Fees are only charged on gains above the high-water mark."
					className="sm:w-[33%]"
				/>
				<PerformanceBreakdownStat
					label="High-Water Mark"
					value={`${highWaterMarkWithCurrentDeposit.prettyPrint()}`}
					marketSymbol={marketSymbol}
					loading={isLoading}
					tooltip={
						'The peak value your deposited assets have reached after fees were last charged. New fees are only applied to gains this point, protecting you from paying twice on the same performance.'
					}
					className="sm:w-[33%]"
				/>
			</div>
		</div>
	);
};

const GRAPH_OPTIONS: { label: string; value: GraphType }[] = [
	{
		label: 'Earnings',
		value: 'pnl',
	},
	{
		label: 'Balance',
		value: 'balance',
	},
];

const PERIOD_OPTIONS: { label: string; value: Period }[] = [
	{
		label: '7D',
		value: '7d',
	},
	{
		label: '30D',
		value: '30d',
	},
	{
		label: '90D',
		value: '90d',
	},
];

export const VaultDepositorPerformanceBreakdown = (props: {
	depositAssetConfig: SpotMarketConfig;
	vaultPubkey: string;
}) => {
	const authority = useCurrentAuthority();
	const { data: depositorAccounts, isLoading: isDepositorLoading } =
		useVaultDepositorAccounts();
	const vaultDepositorAccountData =
		depositorAccounts?.[props.vaultPubkey]?.vaultDepositor;
	const isVaultDepositorLoaded = !isDepositorLoading;

	const [selectedGraph, setSelectedGraph] = useState(GRAPH_OPTIONS[0].value);
	const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_OPTIONS[0].value);

	const isNotVaultDepositor =
		!authority ||
		(isVaultDepositorLoaded &&
			(!vaultDepositorAccountData ||
				vaultDepositorAccountData.netDeposits.eqn(0)));

	const isMobile = useIsMobileScreenSize();

	return (
		<div className="flex flex-col gap-y-2xl rounded-sm border border-none bg-container-bg p-4 sm:mx-0 sm:w-full md:p-6">
			<div className="flex flex-col gap-y-lg">
				<span className="typo-t2">Performance Breakdown</span>
				<UserPerformanceBreakdownStats
					depositAssetConfig={props.depositAssetConfig}
					vaultPubkey={props.vaultPubkey}
				/>
			</div>

			{!isNotVaultDepositor && (
				<>
					<Divider />
					<div className="flex items-center justify-between">
						<Tabs
							size="sm"
							variant="secondary"
							className="w-full sm:w-fit"
							options={GRAPH_OPTIONS}
							activeTab={selectedGraph}
							onTabChange={(value) => setSelectedGraph(value)}
						/>
						{!isMobile && (
							<Tabs
								size="sm"
								variant="secondary"
								options={PERIOD_OPTIONS}
								activeTab={selectedPeriod}
								onTabChange={(value) => setSelectedPeriod(value)}
							/>
						)}
					</div>
					<VaultDepositorHistoryGraph
						vaultPubkey={props.vaultPubkey}
						depositAssetConfig={props.depositAssetConfig}
						graphType={selectedGraph}
						period={selectedPeriod}
					/>
					{isMobile && (
						<Tabs
							size="sm"
							variant="secondary"
							options={PERIOD_OPTIONS}
							activeTab={selectedPeriod}
							onTabChange={(value) => setSelectedPeriod(value)}
						/>
					)}
				</>
			)}
		</div>
	);
};
